import { Card, Divider, IconButton, Skeleton } from '@mui/material';
import { Box, Stack } from '@mui/system';
import React, { useRef, useState } from 'react';
import classes from '../therapyPlanning/input.module.css';
import { PencilIcon } from './commonComponent/therapy-planning-icons';

type templateProps = {
  viewMode?: boolean;
  therapyTemplateData: any;
  setTherapyTemplateData: any;
  templatesLoading?: boolean;
};

type singleCellType = {
  minutes: number;
  index: number;
  type: 'individual' | 'concurrent' | 'group';
};

const TherapyPlanningTemplateSheet = (templateProps: templateProps) => {
  const { viewMode, therapyTemplateData, setTherapyTemplateData, templatesLoading } = templateProps;
  const [cellItemData, setCellItemData] = useState<any>('');
  const [indexToShowIcon, setIndexToShowIcon] = useState('');

  const [editingCell, setEditingCell] = useState<{
    minutes: number;
    index: number;
    type: 'individual' | 'concurrent' | 'group';
  } | null>(null);

  const [isHoveredInd, setIsHoveredInd] = useState(false);
  const [isHoveredCon, setIsHoveredCon] = useState(false);
  const [isHoveredGroup, setIsHoveredGroup] = useState(false);

  const inputRefs1 = useRef<(HTMLInputElement | null)[]>([]);
  const inputRefs2 = useRef<(HTMLInputElement | null)[]>([]);
  const inputRefs3 = useRef<(HTMLInputElement | null)[]>([]);

  const updateMinutes = (cellData: singleCellType) => {
    if (cellData.minutes === cellItemData.minutes) return;

    let therapyType = cellItemData.type?.toUpperCase();

    setTherapyTemplateData((prevState: any) => {
      return {
        ...prevState,
        template: {
          ...prevState.template,
          [therapyType]: prevState.template[therapyType].map((item: any) =>
            item.index === cellItemData.index ? { ...item, minutes: cellItemData?.minutes } : item
          ),
        },
      };
    });
  };

  const handleBlurCell = (currentCellData: singleCellType) => {
    updateMinutes(currentCellData);
    setCellItemData('');
    setEditingCell(null);
  };

  const handleEditCell = (
    e: any,
    cellData: any,
    index: number,
    type: 'individual' | 'concurrent' | 'group'
  ) => {
    e.stopPropagation();
    setCellItemData(cellData);
    setEditingCell({ minutes: cellData.minutes, index, type });
  };

  const handleChangeInput = (e: any) => {
    if (
      (/^[0-9]*$/.test(e.target.value) &&
        parseInt(e.target.value) >= 0 &&
        parseInt(e.target.value) <= 480) ||
      e.target.value?.length === 0
    )
      setCellItemData({
        ...cellItemData,
        minutes: e.target.value,
      });
  };

  const getNextCellItem = (type: 'individual' | 'concurrent' | 'group', nextIndex: number) => {
    const dataMap = new Map<string, any>([
      ['individual', therapyTemplateData?.template?.INDIVIDUAL],
      ['concurrent', therapyTemplateData?.template?.CONCURRENT],
      ['group', therapyTemplateData?.template?.GROUP],
    ]);

    return dataMap.get(type)?.[nextIndex];
  };

  const getCurrentItemData = (
    type: 'individual' | 'concurrent' | 'group',
    eventType: 'ArrowUp' | 'ArrowDown',
    index: number
  ) => {
    const therapyMapping = new Map([
      [
        'individual',
        new Map([
          ['ArrowUp', null],
          ['ArrowDown', therapyTemplateData?.template?.CONCURRENT],
        ]),
      ],
      [
        'concurrent',
        new Map([
          ['ArrowUp', therapyTemplateData?.template?.INDIVIDUAL],
          ['ArrowDown', therapyTemplateData?.template?.GROUP],
        ]),
      ],
      [
        'group',
        new Map([
          ['ArrowUp', therapyTemplateData?.template?.CONCURRENT],
          ['ArrowDown', null],
        ]),
      ],
    ]);

    const eventMapping = therapyMapping.get(type)?.get(eventType);
    return eventMapping ? eventMapping[index] : undefined;
  };

  const getNextUpDownType = (
    type: 'individual' | 'concurrent' | 'group',
    eventType: 'ArrowUp' | 'ArrowDown'
  ) => {
    const transitions = new Map<string, Map<string, string | undefined>>([
      [
        'individual',
        new Map([
          ['ArrowUp', undefined],
          ['ArrowDown', 'concurrent'],
        ]),
      ],
      [
        'concurrent',
        new Map([
          ['ArrowUp', 'individual'],
          ['ArrowDown', 'group'],
        ]),
      ],
      [
        'group',
        new Map([
          ['ArrowUp', 'concurrent'],
          ['ArrowDown', undefined],
        ]),
      ],
    ]);

    return transitions.get(type)?.get(eventType);
  };

  const handleKeyPress = (
    event: React.KeyboardEvent<HTMLInputElement>,
    index: number,
    list: 'list1' | 'list2' | 'list3',
    currentCellData: singleCellType,
    type: any
  ) => {
    const currentRefs = list === 'list1' ? inputRefs1 : list === 'list2' ? inputRefs2 : inputRefs3;
    if (event.key === 'Enter') {
      updateMinutes(currentCellData);
    }
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      event.preventDefault();

      const nextIndex: any =
        event.key === 'ArrowRight'
          ? index !== 6
            ? index + 1
            : index
          : index !== 0
          ? index - 1
          : index;
      updateMinutes(currentCellData);
      setCellItemData(getNextCellItem(type, nextIndex));
      setEditingCell({
        minutes: getNextCellItem(type, nextIndex)?.minutes,
        index: nextIndex,
        type,
      });
      if (nextIndex >= 0 && nextIndex < currentRefs.current.length) {
        currentRefs.current[nextIndex]?.focus();
        // handleBlurCell(individual);
      }
    }

    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault();
      let targetRefs: React.MutableRefObject<(HTMLInputElement | null)[]> | null = null;

      if (event.key === 'ArrowUp') {
        if (list === 'list2') {
          targetRefs = inputRefs1;
        } else if (list === 'list3') {
          targetRefs = inputRefs2;
        }
      }

      if (event.key === 'ArrowDown') {
        if (list === 'list1') {
          targetRefs = inputRefs2;
        } else if (list === 'list2') {
          targetRefs = inputRefs3;
        }
      }

      if (getNextUpDownType(type, event.key)) {
        updateMinutes(currentCellData);

        setCellItemData(getCurrentItemData(type, event.key, index));
        setEditingCell({
          minutes: getCurrentItemData(type, event.key, index)?.minutes || 0,
          index: index,
          type: getNextUpDownType(type, event.key) as any,
        });
      }

      if (targetRefs) {
        targetRefs.current[index]?.focus();
      }
    }
  };

  return (
    <>
      <Card
        sx={{
          my: 1,
        }}
      >
        <Stack
          flexDirection="row"
          gap={'14px'}
        >
          <Stack
            sx={{
              height: '100%',
              borderRight: '1px solid #E5E7EB',
              backgroundColor: '#F8F9FA',
              maxWidth: '150px',
              minWidth: '150px',
            }}
            flex=".982"
          >
            <Stack
              justifyContent="center"
              sx={{
                padding: '10px',
                height: '33.3%',
                fontWeight: '500',
                fontSize: '12px',
                color: '#6C737F',
              }}
            >
              {'Days'}
            </Stack>
          </Stack>
          <Stack
            flexDirection="row"
            sx={{ width: '100%' }}
          >
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']?.map((day: any) => {
              return (
                <Stack
                  sx={{
                    width: `calc(100% / 7)`,
                    position: 'relative',
                    borderRight: '1px solid var(--divider, #F2F4F7)',
                    fontSize: '15px',
                    fontWeight: '500',
                    backgroundColor: 'white',
                    transition: 'transform 0.3s, background-color 0.3s',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyItems: 'center',
                      alignItems: 'center',
                      width: '-webkit-fill-available',
                      justifyContent: 'center',
                      gap: 0.2,
                      height: '100%',
                      pointerEvents: 'auto',
                    }}
                  >
                    <Box
                      sx={{
                        position: 'relative',
                      }}
                    >
                      {day}
                    </Box>
                  </Box>
                </Stack>
              );
            })}
          </Stack>
        </Stack>
        <Stack
          sx={{ height: '132px' }}
          flexDirection="row"
        >
          <Stack
            sx={{ width: '100%' }}
            flexDirection="row"
            flex={7.5}
          >
            <Stack
              sx={{
                height: '100%',
                borderRight: '1px solid #E5E7EB',
                backgroundColor: '#F8F9FA',
                maxWidth: '150px',
                minWidth: '150px',
              }}
              flex=".982"
            >
              {['Individual', 'Concurrent', 'Group']?.map((info: string) => {
                return (
                  <Stack
                    key={info}
                    justifyContent="center"
                    sx={{
                      padding: '10px',
                      height: '33.3%',
                      fontWeight: '500',
                      fontSize: '12px',
                      color: '#6C737F',
                    }}
                  >
                    {info}
                  </Stack>
                );
              })}
            </Stack>
            <Divider
              sx={{
                width: '5px',
                bgcolor: 'rgba(103, 80, 164, 1)',
                borderRadius: '8px 0px 0px 8px',
                background: 'var(--Schemes-Surface-Tint, #6750A4)',
              }}
            />
            <Box
              sx={{
                position: 'relative',
                top: '44%',
                width: '8px',
                height: '10px',
                clipPath: 'polygon(0% 0%,100% 46%,100% 54%, 0% 100%)',
                background: 'var(--Schemes-Surface-Tint, #6750A4)',
              }}
            />

            <Stack sx={{ flex: '8.5' }}>
              {templatesLoading ? (
                <Stack gap="1.5rem">
                  <Skeleton
                    height={25}
                    variant="rounded"
                  />
                  <Skeleton
                    variant="rounded"
                    height={25}
                    animation="wave"
                  />
                  <Skeleton
                    variant="rounded"
                    height={25}
                  />
                </Stack>
              ) : (
                <>
                  <Stack
                    flexDirection="row"
                    sx={{ width: '100%', height: '33.3%' }}
                  >
                    {therapyTemplateData?.template?.INDIVIDUAL?.map(
                      (individual: any, index: any) => {
                        return (
                          <Stack
                            justifyContent="center"
                            alignItems="center"
                            sx={{
                              width: `calc(100% / 7)`,
                              position: 'relative',
                              borderLeft: '#E41000',
                              borderRight: '1px solid var(--divider, #F2F4F7)',
                              fontSize: '14px',
                              fontWeight: '400',
                              color: '#6C737F',
                              cursor: 'pointer',
                              backgroundColor: 'white',
                              transition: 'transform 0.3s, background-color 0.3s',
                            }}
                            onMouseEnter={() => {
                              setIsHoveredInd(true);
                              setIndexToShowIcon(individual?.index);
                            }}
                            onMouseLeave={() => {
                              setIsHoveredInd(false);
                              setIndexToShowIcon('');
                            }}
                            onClick={(e) => handleEditCell(e, individual, index, 'individual')}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                width: '-webkit-fill-available',
                                justifyContent: 'center',
                                gap: 0.2,
                                height: '100%',
                                pointerEvents: 'auto',
                              }}
                            >
                              {!viewMode &&
                                isHoveredInd &&
                                indexToShowIcon === individual?.index &&
                                editingCell?.index !== individual?.index && (
                                  <IconButton
                                    onClick={(e) =>
                                      handleEditCell(e, individual, index, 'individual')
                                    }
                                    size="small"
                                  >
                                    <PencilIcon />
                                  </IconButton>
                                )}

                              {!viewMode &&
                              editingCell?.type === 'individual' &&
                              editingCell?.index === individual?.index ? (
                                <Box
                                  sx={{
                                    backgroundColor: 'white',
                                    width: '70px',

                                    height: '28px',
                                    borderRadius: '8px',
                                    border: '0.5px solid #6366F1',
                                    background: '#FFF',
                                    display: 'flex',
                                    alignItems: 'center',
                                  }}
                                >
                                  <input
                                    type="text"
                                    value={cellItemData?.minutes || ''}
                                    onBlur={() => handleBlurCell(individual)}
                                    key={`list1-${index}`}
                                    onKeyDown={(e) =>
                                      handleKeyPress(e, index, 'list1', individual, 'individual')
                                    }
                                    ref={(el) => (inputRefs1.current[index] = el)}
                                    onChange={(e) => handleChangeInput(e)}
                                    style={{
                                      borderWidth: '0px',
                                      width: '40%',
                                      backgroundColor: 'white',
                                      height: '20px',
                                      textAlign: 'center',
                                      outline: 'none',
                                      borderRadius: '8px',
                                      borderLeft: 'none',
                                    }}
                                    className={classes.input}
                                    autoFocus
                                  />
                                </Box>
                              ) : (
                                <Box
                                  sx={{
                                    position: 'relative',
                                  }}
                                >
                                  {individual?.minutes ? individual?.minutes : '00'}{' '}
                                </Box>
                              )}
                            </Box>
                          </Stack>
                        );
                      }
                    )}
                  </Stack>
                  <Stack
                    flexDirection="row"
                    sx={{ width: '100%', height: '33.3%' }}
                  >
                    {therapyTemplateData?.template?.CONCURRENT?.map(
                      (concurrent: any, index: any) => {
                        return (
                          <Stack
                            justifyContent="center"
                            alignItems="center"
                            sx={{
                              width: `calc(100% / 7)`,
                              position: 'relative',
                              borderLeft: '#E41000',
                              borderRight: '1px solid var(--divider, #F2F4F7)',
                              fontSize: '14px',
                              fontWeight: '400',
                              color: '#6C737F',
                              cursor: 'pointer',
                              backgroundColor: 'white',
                              transition: 'transform 0.3s, background-color 0.3s',
                            }}
                            onMouseEnter={() => {
                              setIsHoveredCon(true);
                              setIndexToShowIcon(concurrent?.index);
                            }}
                            onMouseLeave={() => {
                              setIsHoveredCon(false);
                              setIndexToShowIcon('');
                            }}
                            onClick={(e) => handleEditCell(e, concurrent, index, 'concurrent')}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                width: '-webkit-fill-available',
                                justifyContent: 'center',
                                gap: 0.2,
                                height: '100%',
                                pointerEvents: 'auto',
                              }}
                            >
                              {!viewMode &&
                                isHoveredCon &&
                                indexToShowIcon === concurrent?.index &&
                                editingCell?.index !== concurrent?.index && (
                                  <IconButton
                                    onClick={(e) =>
                                      handleEditCell(e, concurrent, index, 'concurrent')
                                    }
                                    size="small"
                                  >
                                    <PencilIcon />
                                  </IconButton>
                                )}

                              {!viewMode &&
                              editingCell?.type === 'concurrent' &&
                              editingCell?.index === concurrent?.index ? (
                                <Box
                                  sx={{
                                    backgroundColor: 'white',
                                    width: '70px',
                                    height: '28px',
                                    borderRadius: '8px',
                                    border: '0.5px solid #6366F1',
                                    background: '#FFF',
                                    display: 'flex',
                                    alignItems: 'center',
                                  }}
                                >
                                  <input
                                    type="text"
                                    value={cellItemData?.minutes || ''}
                                    onBlur={() => handleBlurCell(concurrent)}
                                    key={`list2-${index}`}
                                    onKeyDown={(e) =>
                                      handleKeyPress(e, index, 'list2', concurrent, 'concurrent')
                                    }
                                    ref={(el) => (inputRefs2.current[index] = el)}
                                    onChange={(e) => handleChangeInput(e)}
                                    style={{
                                      borderWidth: '0px',
                                      width: '40%',
                                      backgroundColor: 'white',
                                      height: '20px',
                                      textAlign: 'center',
                                      outline: 'none',
                                      borderRadius: '8px',
                                      borderLeft: 'none',
                                    }}
                                    className={classes.input}
                                    autoFocus
                                  />
                                </Box>
                              ) : (
                                <Box
                                  sx={{
                                    position: 'relative',
                                  }}
                                >
                                  {concurrent?.minutes ? concurrent?.minutes : '00'}{' '}
                                </Box>
                              )}
                            </Box>
                          </Stack>
                        );
                      }
                    )}
                  </Stack>
                  <Stack
                    flexDirection="row"
                    sx={{ width: '100%', height: '33.3%' }}
                  >
                    {therapyTemplateData?.template?.GROUP?.map((group: any, index: any) => {
                      return (
                        <Stack
                          justifyContent="center"
                          alignItems="center"
                          sx={{
                            width: `calc(100% / 7)`,
                            position: 'relative',
                            borderLeft: '#E41000',
                            borderRight: '1px solid var(--divider, #F2F4F7)',
                            fontSize: '14px',
                            fontWeight: '400',
                            color: '#6C737F',
                            cursor: 'pointer',
                            backgroundColor: 'white',
                            transition: 'transform 0.3s, background-color 0.3s',
                          }}
                          onMouseEnter={() => {
                            setIsHoveredGroup(true);
                            setIndexToShowIcon(group?.index);
                          }}
                          onMouseLeave={() => {
                            setIsHoveredGroup(false);
                            setIndexToShowIcon('');
                          }}
                          onClick={(e) => handleEditCell(e, group, index, 'group')}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              width: '-webkit-fill-available',
                              justifyContent: 'center',
                              gap: 0.2,
                              height: '100%',
                              pointerEvents: 'auto',
                            }}
                          >
                            {!viewMode &&
                              isHoveredGroup &&
                              indexToShowIcon === group?.index &&
                              editingCell?.index !== group?.index && (
                                <IconButton
                                  onClick={(e) => handleEditCell(e, group, index, 'group')}
                                  size="small"
                                >
                                  <PencilIcon />
                                </IconButton>
                              )}

                            {!viewMode &&
                            editingCell?.type === 'group' &&
                            editingCell?.index === group?.index ? (
                              <Box
                                sx={{
                                  backgroundColor: 'white',
                                  width: '70px',

                                  height: '28px',
                                  borderRadius: '8px',
                                  border: '0.5px solid #6366F1',
                                  background: '#FFF',
                                  display: 'flex',
                                  alignItems: 'center',
                                }}
                              >
                                <input
                                  type="text"
                                  value={cellItemData?.minutes || ''}
                                  onBlur={() => handleBlurCell(group)}
                                  key={`list3-${index}`}
                                  onKeyDown={(e) =>
                                    handleKeyPress(e, index, 'list3', group, 'group')
                                  }
                                  ref={(el) => (inputRefs3.current[index] = el)}
                                  onChange={(e) => handleChangeInput(e)}
                                  style={{
                                    borderWidth: '0px',
                                    width: '40%',
                                    backgroundColor: 'white',
                                    height: '20px',
                                    textAlign: 'center',
                                    outline: 'none',
                                    borderRadius: '8px',
                                    borderLeft: 'none',
                                  }}
                                  className={classes.input}
                                  autoFocus
                                />
                              </Box>
                            ) : (
                              <Box
                                sx={{
                                  position: 'relative',
                                }}
                              >
                                {group?.minutes ? group?.minutes : '00'}{' '}
                              </Box>
                            )}
                          </Box>
                        </Stack>
                      );
                    })}
                  </Stack>
                </>
              )}
            </Stack>
          </Stack>
        </Stack>
      </Card>
    </>
  );
};

export default TherapyPlanningTemplateSheet;
